import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { exportDewarsToExcel } from '../utils/excelExport';
import { dewarService } from '../services/dewarServiceAppwrite';
import OptimizedDewarCard from '../components/OptimizedDewarCard';
import { performanceMonitor } from '../utils/performanceMonitor';

const Dewars = () => {
  const navigate = useNavigate();
  const [dewars, setDewars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // OPTIMIZATION: Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); // Show 5 dewars per page

  // OPTIMIZATION: Track component performance
  React.useEffect(() => {
    performanceMonitor?.logComponentRender('Dewars Page');
  }, []);

  // OPTIMIZATION: Memoize filtered dewars to avoid recalculating on every render
  const filteredDewars = React.useMemo(() => {
    if (!searchTerm) return dewars;
    return dewars?.filter(dewar => 
      dewar?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      dewar?.location?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    );
  }, [dewars, searchTerm]);

  // Load dewars from Supabase on mount
  useEffect(() => {
    loadDewars();
  }, []);

  // OPTIMIZATION: Memoize loadDewars function
  const loadDewars = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await dewarService?.getAll();
      setDewars(data);
    } catch (err) {
      console.error('Error loading dewars:', err);
      setError(err?.message || 'Failed to load dewars');
    } finally {
      setLoading(false);
    }
  }, []);

  const [expandedDewar, setExpandedDewar] = useState(null);
  const [expandedRack, setExpandedRack] = useState(null);
  const [isDewarModalOpen, setIsDewarModalOpen] = useState(false);
  const [isEditDewarModalOpen, setIsEditDewarModalOpen] = useState(false);
  const [isLevelHistoryModalOpen, setIsLevelHistoryModalOpen] = useState(false);
  const [selectedDewarId, setSelectedDewarId] = useState(null);
  const [selectedDewarForHistory, setSelectedDewarForHistory] = useState(null);

  const [dewarFormData, setDewarFormData] = useState({
    name: '',
    location: '',
    temp: '-196°C',
    status: 'Operational',
    liquidNitrogenLevel: 25
  });

  // Add this block - Missing state declaration
  const [sampleFormData, setSampleFormData] = useState({});

  // OPTIMIZATION: Memoize expensive calculations
  const calculateDewarStats = useCallback((dewar) => {
    const totalRacks = dewar?.racks?.length || 0;
    const maxRacks = 6;
    const totalBoxes = dewar?.racks?.reduce((sum, rack) => sum + (rack?.boxes?.length || 0), 0) || 0;
    const maxBoxes = 60;
    const occupiedBoxes = dewar?.racks?.reduce((sum, rack) => {
      return sum + (rack?.boxes?.filter(box => (box?.occupied || 0) > 0)?.length || 0);
    }, 0) || 0;
    const totalSamples = dewar?.racks?.reduce((rackSum, rack) => {
      return rackSum + (rack?.boxes?.reduce((boxSum, box) => boxSum + (box?.occupied || 0), 0) || 0);
    }, 0) || 0;
    const maxSamples = dewar?.racks?.reduce((rackSum, rack) => {
      return rackSum + (rack?.boxes?.reduce((boxSum, box) => boxSum + (box?.capacity || 0), 0) || 0);
    }, 0) || 0;

    return { totalRacks, maxRacks, totalBoxes, maxBoxes, occupiedBoxes, totalSamples, maxSamples };
  }, []);

  const calculateRackStats = useCallback((rack) => {
    const totalBoxes = rack?.boxes?.length || 0;
    const maxBoxes = 10;
    const occupiedBoxes = rack?.boxes?.filter(box => (box?.occupied || 0) > 0)?.length || 0;
    const totalSamples = rack?.boxes?.reduce((sum, box) => sum + (box?.occupied || 0), 0) || 0;
    const maxSamples = rack?.boxes?.reduce((sum, box) => sum + (box?.capacity || 0), 0) || 0;

    return { totalBoxes, maxBoxes, occupiedBoxes, totalSamples, maxSamples };
  }, []);

  // OPTIMIZATION: Paginate dewars to reduce initial render load
  const paginatedDewars = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredDewars?.slice(startIndex, endIndex);
  }, [filteredDewars, currentPage, itemsPerPage]);

  const totalPages = useMemo(() => Math.ceil(filteredDewars?.length / itemsPerPage), [filteredDewars?.length, itemsPerPage]);

  const handleDewarInputChange = (e) => {
    const { name, value } = e?.target || {};
    setDewarFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSampleInputChange = (e) => {
    const { name, value } = e?.target || {};
    setSampleFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddDewar = async (e) => {
    e?.preventDefault();
    try {
      setError('');
      await dewarService?.create(dewarFormData);
      await loadDewars();
      setIsDewarModalOpen(false);
      setDewarFormData({ name: '', location: '', temp: '-196°C', status: 'Operational', liquidNitrogenLevel: 25 });
    } catch (err) {
      console.error('Error creating dewar:', err);
      setError(err?.message || 'Failed to create dewar');
    }
  };

  const handleEditDewar = (dewar) => {
    setSelectedDewarId(dewar?.id);
    setDewarFormData({
      name: dewar?.name,
      location: dewar?.location || '',
      temp: dewar?.temp,
      status: dewar?.status,
      liquidNitrogenLevel: dewar?.liquidNitrogenLevel || 14
    });
    setIsEditDewarModalOpen(true);
  };

  const handleUpdateDewar = async (e) => {
    e?.preventDefault();
    try {
      setError('');
      await dewarService?.update(selectedDewarId, dewarFormData);
      await loadDewars();
      setIsEditDewarModalOpen(false);
      setDewarFormData({ name: '', location: '', temp: '-196°C', status: 'Operational', liquidNitrogenLevel: 25 });
      setSelectedDewarId(null);
    } catch (err) {
      console.error('Error updating dewar:', err);
      setError(err?.message || 'Failed to update dewar');
    }
  };

  const handleDeleteDewar = async (id) => {
    if (window?.confirm('Are you sure you want to delete this dewar? All racks and boxes will be removed.')) {
      try {
        setError('');
        await dewarService?.delete(id);
        await loadDewars();
      } catch (err) {
        console.error('Error deleting dewar:', err);
        setError(err?.message || 'Failed to delete dewar');
      }
    }
  };

  const handleDeleteBox = async (dewarId, rackId, boxId) => {
    if (window?.confirm('Are you sure you want to delete this box?')) {
      try {
        setError('');
        await dewarService?.deleteBox(boxId);
        await loadDewars();
      } catch (err) {
        console.error('Error deleting box:', err);
        setError(err?.message || 'Failed to delete box');
      }
    }
  };

  const openGridView = (dewar, rack, box) => {
    navigate('/box-view-sample-management', {
      state: {
        boxData: {
          dewarId: dewar?.id,
          rackId: rack?.id,
          boxId: box?.id,
          boxName: box?.name,
          dewarName: dewar?.name,
          rackNumber: rack?.number
        }
      }
    });
  };

  const toggleRackExpansion = (rackId) => {
    setExpandedRack(expandedRack === rackId ? null : rackId);
  };

  const openLevelHistoryModal = (dewar) => {
    setSelectedDewarForHistory(dewar);
    setIsLevelHistoryModalOpen(true);
  };

  const closeLevelHistoryModal = () => {
    setIsLevelHistoryModalOpen(false);
    setSelectedDewarForHistory(null);
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dewars...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dewars Management</h1>
              <p className="text-sm text-gray-600">
                Hierarchy: Dewar → 6 Racks (numbered 1-6) → 10 Boxes per rack (labeled 1A-1J, 2A-2J, etc.) → Samples
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => exportDewarsToExcel(dewars)}
                className="bg-green-600 hover:bg-green-700 text-white flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Export All to Excel
              </Button>
              <Button
                onClick={() => setIsDewarModalOpen(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                Add New Dewar
              </Button>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search dewars by name or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full sm:w-64"
            />
          </div>
        </div>

        {filteredDewars?.length > itemsPerPage && (
          <div className="mb-4 flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
            <p className="text-sm text-gray-600">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredDewars?.length)} of {filteredDewars?.length} dewars
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-indigo-600 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1 bg-gray-100 rounded">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 bg-indigo-600 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          </div>
        ) : error ? (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        ) : filteredDewars?.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No dewars found. Try adjusting your search or add a new dewar.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedDewars?.map(dewar => (
              <OptimizedDewarCard key={dewar?.id} dewar={dewar} />
            ))}
          </div>
        )}
      </main>
      {/* Add Dewar Modal */}
      {isDewarModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Add New Dewar</h2>
              <button
                onClick={() => setIsDewarModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleAddDewar} className="space-y-4">
              <Input
                label="Dewar Name"
                name="name"
                value={dewarFormData?.name}
                onChange={handleDewarInputChange}
                required
                placeholder="Enter dewar name"
              />

              <Input
                label="Location"
                name="location"
                value={dewarFormData?.location}
                onChange={handleDewarInputChange}
                placeholder="e.g., Lab A - Room 101"
              />

              <Input
                label="Temperature"
                name="temp"
                value={dewarFormData?.temp}
                onChange={handleDewarInputChange}
                required
                placeholder="e.g., -196°C"
              />

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Liquid Nitrogen Level (inches)
                </label>
                <Input
                  type="number"
                  name="liquidNitrogenLevel"
                  value={dewarFormData?.liquidNitrogenLevel}
                  onChange={handleDewarInputChange}
                  required
                  min="0"
                  max="22"
                  step="0.5"
                  placeholder="0-22 inches"
                />
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      type="range"
                      name="liquidNitrogenLevel"
                      value={dewarFormData?.liquidNitrogenLevel}
                      onChange={handleDewarInputChange}
                      min="0"
                      max="22"
                      step="0.5"
                      className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-indigo-600 [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-indigo-600 [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
                    />
                    <div className="absolute top-0 left-0 w-full flex justify-between text-xs text-gray-500 mt-3 pointer-events-none">
                      <span>0"</span>
                      <span>22"</span>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-gray-700 min-w-[60px]">
                    {dewarFormData?.liquidNitrogenLevel}"
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Current level: {dewarFormData?.liquidNitrogenLevel}" (
                  {parseFloat(dewarFormData?.liquidNitrogenLevel) <= 4
                    ? 'Critical'
                    : parseFloat(dewarFormData?.liquidNitrogenLevel) <= 8
                    ? 'Low' :'Good'
                  })
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Status</label>
                <select
                  name="status"
                  value={dewarFormData?.status}
                  onChange={handleDewarInputChange}
                  className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                >
                  <option value="Operational">Operational</option>
                  <option value="Warning">Warning</option>
                  <option value="Maintenance">Maintenance</option>
                </select>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Note:</strong> New dewars will be created with 6 racks (numbered 1-6), each containing 10 empty boxes (labeled 1A-1J, 2A-2J, etc.)
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setIsDewarModalOpen(false)}
                  className="flex-1 bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-indigo-600 text-white hover:bg-indigo-700"
                >
                  Add Dewar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Edit Dewar Modal */}
      {isEditDewarModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Edit Dewar</h2>
              <button
                onClick={() => {
                  setIsEditDewarModalOpen(false);
                  setDewarFormData({ name: '', location: '', temp: '-196°C', status: 'Operational', liquidNitrogenLevel: 25 });
                  setSelectedDewarId(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleUpdateDewar} className="space-y-4">
              <Input
                label="Dewar Name"
                name="name"
                value={dewarFormData?.name}
                onChange={handleDewarInputChange}
                required
                placeholder="Enter dewar name"
              />

              <Input
                label="Location"
                name="location"
                value={dewarFormData?.location}
                onChange={handleDewarInputChange}
                placeholder="e.g., Lab A - Room 101"
              />

              <Input
                label="Temperature"
                name="temp"
                value={dewarFormData?.temp}
                onChange={handleDewarInputChange}
                required
                placeholder="e.g., -196°C"
              />

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Liquid Nitrogen Level (inches)
                </label>
                <Input
                  type="number"
                  name="liquidNitrogenLevel"
                  value={dewarFormData?.liquidNitrogenLevel}
                  onChange={handleDewarInputChange}
                  required
                  min="0"
                  max="22"
                  step="0.5"
                  placeholder="0-22 inches"
                />
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      type="range"
                      name="liquidNitrogenLevel"
                      value={dewarFormData?.liquidNitrogenLevel}
                      onChange={handleDewarInputChange}
                      min="0"
                      max="22"
                      step="0.5"
                      className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-indigo-600 [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-indigo-600 [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
                    />
                    <div className="absolute top-0 left-0 w-full flex justify-between text-xs text-gray-500 mt-3 pointer-events-none">
                      <span>0"</span>
                      <span>22"</span>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-gray-700 min-w-[60px]">
                    {dewarFormData?.liquidNitrogenLevel}"
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Current level: {dewarFormData?.liquidNitrogenLevel}" (
                  {parseFloat(dewarFormData?.liquidNitrogenLevel) <= 4
                    ? 'Critical'
                    : parseFloat(dewarFormData?.liquidNitrogenLevel) <= 8
                    ? 'Low' :'Good'
                  })
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Status</label>
                <select
                  name="status"
                  value={dewarFormData?.status}
                  onChange={handleDewarInputChange}
                  className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                >
                  <option value="Operational">Operational</option>
                  <option value="Warning">Warning</option>
                  <option value="Maintenance">Maintenance</option>
                </select>
              </div>

              <div className="bg-amber-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Note:</strong> Editing dewar information will not affect existing racks, boxes, or samples.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => {
                    setIsEditDewarModalOpen(false);
                    setDewarFormData({ name: '', location: '', temp: '-196°C', status: 'Operational', liquidNitrogenLevel: 25 });
                    setSelectedDewarId(null);
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
                >
                  Update Dewar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Liquid Nitrogen Level History Modal */}
      {isLevelHistoryModalOpen && selectedDewarForHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Liquid Nitrogen Level History
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedDewarForHistory?.name} - Current Level: {selectedDewarForHistory?.liquidNitrogenLevel}"
                  </p>
                </div>
                <button
                  onClick={closeLevelHistoryModal}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {selectedDewarForHistory?.levelHistory && selectedDewarForHistory?.levelHistory?.length > 0 ? (
                <div className="space-y-3">
                  {[...selectedDewarForHistory?.levelHistory]?.reverse()?.map((entry, index) => {
                    const isIncrease = entry?.previousLevel !== null && entry?.level > entry?.previousLevel;
                    const isDecrease = entry?.previousLevel !== null && entry?.level < entry?.previousLevel;
                    
                    return (
                      <div 
                        key={entry?.id} 
                        className={`p-4 rounded-lg border-l-4 ${
                          isIncrease 
                            ? 'bg-green-50 border-green-500' 
                            : isDecrease 
                            ? 'bg-yellow-50 border-yellow-500' :'bg-blue-50 border-blue-500'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-1 text-xs font-semibold rounded ${
                                isIncrease 
                                  ? 'bg-green-100 text-green-800' 
                                  : isDecrease 
                                  ? 'bg-yellow-100 text-yellow-800' :'bg-blue-100 text-blue-800'
                              }`}>
                                {entry?.action}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatDate(entry?.date)}
                              </span>
                            </div>
                            <p className="text-sm font-semibold text-gray-900">
                              {entry?.previousLevel !== null ? (
                                <>
                                  {entry?.previousLevel}" → {entry?.level}"
                                  <span className={`ml-2 text-xs ${
                                    isIncrease ? 'text-green-600' : 'text-yellow-600'
                                  }`}>
                                    ({isIncrease ? '+' : ''}{(entry?.level - entry?.previousLevel)?.toFixed(1)}")
                                  </span>
                                </>
                              ) : (
                                `Initial Level: ${entry?.level}"`
                              )}
                            </p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mt-2">
                          <div>
                            <span className="font-semibold">Checked By:</span> {entry?.checkedBy}
                          </div>
                          <div>
                            <span className="font-semibold">Status:</span>{' '}
                            <span className={`font-medium ${
                              entry?.level <= 4 
                                ? 'text-red-600' 
                                : entry?.level <= 8 
                                ? 'text-yellow-600' :'text-green-600'
                            }`}>
                              {entry?.level <= 4 
                                ? 'Critical' 
                                : entry?.level <= 8 
                                ? 'Low' :'Good'
                              }
                            </span>
                          </div>
                        </div>
                        
                        {entry?.notes && (
                          <div className="mt-2 text-xs text-gray-700 bg-white p-2 rounded">
                            <span className="font-semibold">Notes:</span> {entry?.notes}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No level history available for this dewar.</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex gap-3">
                <Button
                  onClick={closeLevelHistoryModal}
                  className="flex-1 bg-gray-600 text-white hover:bg-gray-700"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(Dewars);